// frontend/packages/business-console/src/pages/GoLiveChecklist.jsx
//
// Work item S7 (spec discovery/13 B1) - the customer go-live checklist. After
// the founder clears a build (approved_for_golive), the customer connects their
// domain here: we render the exact DNS records to add at their registrar
// (routing CNAME/ALIAS to the prod Front Door endpoint + the _dnsauth TXT
// validation records, with current tokens), they add them, then click "take my
// site live". That moves the build to dns_pending and a background worker waits
// for validation, publishes to prod, and emails them when live.
//
// ⚠ Console builds are NOT runnable in the Claude sandbox - a founder eyeball
// is owed on this page (the 5.10 lesson).
//
// Copy rule: every string here is customer-visible. No em-dashes.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

import {
  getBuildGoLive,
  publishBuildSite,
} from '../services/buildFunnelService';

function errMessage(e) {
  return (
    (e && e.response && e.response.data && e.response.data.error) ||
    'Something went wrong. Try again.'
  );
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value || ''));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked; the value is selectable inline */
    }
  };
  return (
    <Tooltip title={copied ? 'Copied' : 'Copy'}>
      <IconButton size="small" onClick={onCopy} aria-label="copy">
        {copied ? (
          <CheckCircleIcon fontSize="inherit" color="success" />
        ) : (
          <ContentCopyIcon fontSize="inherit" />
        )}
      </IconButton>
    </Tooltip>
  );
}

function ScopeStatus({ scope, state }) {
  const label = scope === 'apex' ? 'Root domain' : 'www';
  if (state === 'Approved') {
    return (
      <Chip
        size="small"
        color="success"
        icon={<CheckCircleIcon />}
        label={`${label}: verified`}
      />
    );
  }
  return (
    <Chip
      size="small"
      variant="outlined"
      label={`${label}: not verified yet`}
    />
  );
}

function RecordsTable({ records }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Name / Host</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {records.map((r, i) => (
          <TableRow key={`${r.scope}-${r.kind}-${i}`}>
            <TableCell>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {r.type}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {r.kind === 'routing'
                  ? 'points your domain at us'
                  : 'proves you own it'}
              </Typography>
            </TableCell>
            <TableCell>
              <code>{r.host}</code>
            </TableCell>
            <TableCell>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  component="code"
                  sx={{
                    wordBreak: 'break-all',
                    fontSize: 13,
                    bgcolor: 'action.hover',
                    px: 0.5,
                    borderRadius: 0.5,
                  }}
                >
                  {r.value || '(pending)'}
                </Box>
                {r.value ? <CopyButton value={r.value} /> : null}
              </Stack>
              {r.note ? (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
                >
                  {r.note}
                </Typography>
              ) : null}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function GoLiveChecklist() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [published, setPublished] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await getBuildGoLive());
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onPublish = async () => {
    setBusy(true);
    setError('');
    try {
      await publishBuildSite();
      setPublished(true);
      await load();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const state = data && data.state;
  const records = (data && data.records) || [];
  const validation = (data && data.validation) || {};
  const apexRecords = records.filter((r) => r.scope === 'apex');
  const wwwRecords = records.filter((r) => r.scope === 'www');

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', py: 3, px: 2 }}>
      <Typography variant="h5" gutterBottom>
        Take your website live
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Connect your domain by adding the records below at your domain provider
        (where you bought your web address). Once they are in, we will publish
        your site and email you when it is live.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      {!data || data.available === false ? (
        <Alert severity="info">
          {data && data.reason === 'awaiting_founder_review'
            ? 'You approved your site. Our team is doing a final check before you can connect your domain. We will let you know shortly.'
            : 'Your site is not ready to go live yet. We will let you know when it is.'}
        </Alert>
      ) : state === 'live' ? (
        <Stack spacing={2}>
          <Alert
            severity="success"
            icon={<CheckCircleIcon fontSize="inherit" />}
          >
            Your website is live
            {data.prodUrl ? (
              <>
                {' '}
                at{' '}
                <Link
                  href={data.prodUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {data.apexDomain}{' '}
                  <OpenInNewIcon
                    sx={{ fontSize: 14, verticalAlign: 'middle' }}
                  />
                </Link>
              </>
            ) : (
              '.'
            )}
          </Alert>
          <Box sx={{ fontSize: 13, color: 'text.secondary' }}>
            Something not looking right on your live site? Email{' '}
            <Link
              href={`mailto:${
                process.env.REACT_APP_SUPPORT_EMAIL || 'support@upryzze.com'
              }?subject=${encodeURIComponent('Help with my live site')}`}
            >
              {process.env.REACT_APP_SUPPORT_EMAIL || 'support@upryzze.com'}
            </Link>{' '}
            and we will take a look.
          </Box>
        </Stack>
      ) : data.incomplete ? (
        <Alert severity="info">
          We are still finishing your site setup. Check back in a few minutes.
        </Alert>
      ) : (
        <Stack spacing={3}>
          {state === 'dns_pending' ? (
            <Alert severity="info">
              Thanks. We are checking your domain records and will publish your
              site automatically once they are verified. This can take anywhere
              from a few minutes to a few hours depending on your provider. You
              can leave this page; we will email you when you are live.
            </Alert>
          ) : null}

          <Card>
            <CardContent>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1, flexWrap: 'wrap' }}
              >
                <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                  Records for {data.apexDomain}
                </Typography>
                <ScopeStatus scope="apex" state={validation.apex} />
                {data.includeWww ? (
                  <ScopeStatus scope="www" state={validation.www} />
                ) : null}
              </Stack>
              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Root domain ({data.apexDomain})
              </Typography>
              <RecordsTable records={apexRecords} />

              {data.includeWww && wwwRecords.length ? (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    www.{data.apexDomain}
                  </Typography>
                  <RecordsTable records={wwwRecords} />
                </>
              ) : null}

              {data.tokensStale ? (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', mt: 2 }}
                >
                  If you just reached this page, give the verification values a
                  moment to finalize, then refresh.
                </Typography>
              ) : null}
            </CardContent>
          </Card>

          {state === 'approved_for_golive' ? (
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  {published ? 'Publishing your site' : 'Ready to go live?'}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {published ? (
                  <Typography variant="body2" color="text.secondary">
                    Payment received. We are verifying your domain and
                    publishing your site now. You do not need to wait here, we
                    will email you when it is live.
                  </Typography>
                ) : (
                  <>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Add the records above at your domain provider, then
                      publish. Publishing takes your site live on{' '}
                      {data && data.apexDomain ? (
                        <strong>{data.apexDomain}</strong>
                      ) : (
                        'your domain'
                      )}{' '}
                      and starts your monthly plan. You can cancel anytime.
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<RocketLaunchIcon />}
                      disabled={busy}
                      onClick={onPublish}
                    >
                      {busy
                        ? 'Publishing...'
                        : data && data.apexDomain
                          ? `Publish to ${data.apexDomain}`
                          : 'Publish and go live'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          ) : null}

          <Box>
            <Button onClick={load} disabled={busy}>
              Refresh status
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
